import type { MorseSymbol } from '@/utils/morse';

export type TimingPhase = 'keydown' | 'keyup' | 'combined';

export interface TimingDelta {
  valuePx: number;          // signed; negative = late, positive = early
  phase: TimingPhase;
  symbol: MorseSymbol;
  letter: string;
}

export interface JudgeFactor {
  id: string;
  label: string;
  value: number;        // The actual median or stddev value
  multiplier: number;
  score: number;        // abs(value) * multiplier
  advice: string;
}

export interface JudgeResult {
  timingDeltas: TimingDelta[];
  factors: JudgeFactor[];
  selected: JudgeFactor | null;
}

interface PendingKeydown {
  offsetPx: number;
  symbol: MorseSymbol;
  letter: string;
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

function mean(values: number[]): number {
  if (!values.length) return 0;
  const sum = values.reduce((acc, v) => acc + v, 0);
  return sum / values.length;
}

function stdDev(values: number[]): number {
  if (values.length < 2) return 0;
  const avg = mean(values);
  const variance = mean(values.map((v) => (v - avg) ** 2));
  return Math.sqrt(variance);
}

export class PerformanceJudgeTracker {
  private timingDeltas: TimingDelta[] = [];
  private pendingKeydown: PendingKeydown | null = null;

  reset(): void {
    this.timingDeltas = [];
    this.pendingKeydown = null;
  }

  recordKeydown(input: { offsetPx: number; symbol: MorseSymbol; letter: string }): void {
    this.pendingKeydown = {
      offsetPx: input.offsetPx,
      symbol: input.symbol,
      letter: input.letter,
    };
  }

  recordKeyup(input: { offsetPx: number; symbol: MorseSymbol; letter: string }): void {
    const pending = this.pendingKeydown;
    this.pendingKeydown = null;
    if (!pending) return;

    const keydownPx = pending.offsetPx;
    const keyupPx = input.offsetPx;
    const combinedPx = (keydownPx + keyupPx) / 2;

    const symbol = pending.symbol || input.symbol;
    const letter = pending.letter || input.letter;

    this.timingDeltas.push(
      { valuePx: keydownPx, phase: 'keydown', symbol, letter },
      { valuePx: keyupPx, phase: 'keyup', symbol, letter },
      { valuePx: combinedPx, phase: 'combined', symbol, letter }
    );
  }

  cancelPending(): void {
    this.pendingKeydown = null;
  }

  getResults(): JudgeResult {
    const factors = this.evaluateFactors(this.timingDeltas);
    const selected = this.selectFactor(factors);
    return {
      timingDeltas: this.timingDeltas,
      factors,
      selected,
    };
  }

  private evaluateFactors(deltas: TimingDelta[]): JudgeFactor[] {
    const factors: JudgeFactor[] = [];
    const filter = (predicate: (d: TimingDelta) => boolean) => deltas.filter(predicate).map((d) => d.valuePx);

    // Helper to create a median factor
    const addMedianFactor = (
      id: string,
      label: string,
      values: number[],
      multiplier: number,
      earlyAdvice: string,
      lateAdvice: string
    ): void => {
      if (!values.length) return;
      const med = median(values);
      const advice = med > 0 ? earlyAdvice : med < 0 ? lateAdvice : 'Timing is centered.';
      factors.push({
        id,
        label,
        value: med,
        multiplier,
        score: Math.abs(med) * multiplier,
        advice,
      });
    };

    // Helper to create a stddev factor
    const addStddevFactor = (
      id: string,
      label: string,
      values: number[],
      multiplier: number,
      advice: string
    ): void => {
      if (!values.length) return;
      const sd = stdDev(values);
      factors.push({
        id,
        label,
        value: sd,
        multiplier,
        score: sd * multiplier,
        advice,
      });
    };

    // Slice the data
    const keydown = filter((d) => d.phase === 'keydown');
    const keyup = filter((d) => d.phase === 'keyup');
    const combined = filter((d) => d.phase === 'combined');
    const dotCombined = filter((d) => d.phase === 'combined' && d.symbol === '.');
    const dashCombined = filter((d) => d.phase === 'combined' && d.symbol === '-');

    // Global timing (6 factors)
    addMedianFactor('keydown_median', 'Keydown median', keydown, 1.0,
      'You press early. Aim later.',
      'You press late. Aim earlier.');
    addStddevFactor('keydown_stddev', 'Keydown stddev', keydown, 1.0,
      'Your press timing is inconsistent.');

    addMedianFactor('keyup_median', 'Keyup median', keyup, 1.0,
      'You release early. Hold longer.',
      'You release late. Let go sooner.');
    addStddevFactor('keyup_stddev', 'Keyup stddev', keyup, 1.0,
      'Your release timing is inconsistent.');

    addMedianFactor('combined_median', 'Combined median', combined, 0.25,
      'Overall timing is early. Aim later.',
      'Overall timing is late. Aim earlier.');
    addStddevFactor('combined_stddev', 'Combined stddev', combined, 0.25,
      'Overall timing is inconsistent.');

    // Symbol timing (4 factors)
    addMedianFactor('dot_combined_median', 'Dot timing median', dotCombined, 1.0,
      'Your dots are early. Delay them slightly.',
      'Your dots are late. Hit them sooner.');
    addStddevFactor('dot_combined_stddev', 'Dot timing stddev', dotCombined, 1.0,
      'Your dot timing is inconsistent.');

    addMedianFactor('dash_combined_median', 'Dash timing median', dashCombined, 1.0,
      'Your dashes are early. Delay them slightly.',
      'Your dashes are late. Hit them sooner.');
    addStddevFactor('dash_combined_stddev', 'Dash timing stddev', dashCombined, 1.0,
      'Your dash timing is inconsistent.');

    // Letter timing (N factors, one per letter)
    factors.push(...this.evaluateLetterFactors(deltas));

    return factors;
  }

  private evaluateLetterFactors(deltas: TimingDelta[]): JudgeFactor[] {
    const combined = deltas.filter((d) => d.phase === 'combined');
    if (!combined.length) return [];

    const byLetter = new Map<string, number[]>();
    for (const d of combined) {
      const letter = d.letter || '?';
      if (!byLetter.has(letter)) {
        byLetter.set(letter, []);
      }
      byLetter.get(letter)?.push(d.valuePx);
    }

    const factors: JudgeFactor[] = [];
    for (const [letter, values] of byLetter.entries()) {
      if (!values.length) continue;
      const med = median(values);
      const advice = med > 0
        ? `Letter "${letter}" is early. Delay it.`
        : med < 0
          ? `Letter "${letter}" is late. Hit it sooner.`
          : `Letter "${letter}" timing is centered.`;
      factors.push({
        id: `letter_${letter.toLowerCase()}_median`,
        label: `Letter ${letter} median`,
        value: med,
        multiplier: 0.5,
        score: Math.abs(med) * 0.5,
        advice,
      });
    }

    return factors;
  }

  private selectFactor(factors: JudgeFactor[]): JudgeFactor | null {
    if (!factors.length) return null;
    // Pick the factor with the largest score. No randomness.
    let best = factors[0];
    for (const f of factors) {
      if (f.score > best.score) {
        best = f;
      }
    }
    return best;
  }
}
